import React, { useState, useEffect } from "react";
import {
  User,
  ChevronDown,
  Menu,
  Eye,
  EyeOff,
  Users,
  KeyRound,
} from "lucide-react";
import UserSidebar from "../UserSidebar";

import {
  getUserInfoById,
  updateInfoUser,
  updatePassword,
} from "../../services/UserService";
import { getMyClasses, joinPrivateClass, leaveMyClass } from "../../services/ClassService";
import { toast } from "react-toastify";
import Loading from "../Loading";
import Footer from "../Footer/Footer";
import { Link } from "react-router-dom";

export default function ProfilePage() {
  const [userInfo, setUserInfo] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    school: "",
    province: "",
  });
  const [loading, setLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [showPassword, setShowPassword] = useState({
    current: false,
    newPw: false,
    confirm: false,
  });
  const [myClasses, setMyClasses] = useState([]);
  const [joinClassForm, setJoinClassForm] = useState({ classCode: "", pin: "" });
  const [joiningClass, setJoiningClass] = useState(false);
  const [leavingClassId, setLeavingClassId] = useState("");
  const [leaveConfirmClass, setLeaveConfirmClass] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const provinces = [
    "Hà Nội",
    "TP. Hồ Chí Minh",
    "Hải Phòng",
    "Đà Nẵng",
    "Cần Thơ",
    "An Giang",
    "Bà Rịa - Vũng Tàu",
    "Bắc Giang",
    "Bắc Kạn",
    "Bạc Liêu",
    "Bắc Ninh",
    "Bến Tre",
    "Bình Định",
    "Bình Dương",
    "Bình Phước",
    "Bình Thuận",
    "Cà Mau",
    "Cao Bằng",
    "Đắk Lắk",
    "Đắk Nông",
    "Điện Biên",
    "Đồng Nai",
    "Đồng Tháp",
    "Gia Lai",
    "Hà Giang",
    "Hà Nam",
    "Hà Tĩnh",
    "Hải Dương",
    "Hậu Giang",
    "Hòa Bình",
    "Hưng Yên",
    "Khánh Hòa",
    "Kiên Giang",
    "Kon Tum",
    "Lai Châu",
    "Lâm Đồng",
    "Lạng Sơn",
    "Lào Cai",
    "Long An",
    "Nam Định",
    "Nghệ An",
    "Ninh Bình",
    "Ninh Thuận",
    "Phú Thọ",
    "Phú Yên",
    "Quảng Bình",
    "Quảng Nam",
    "Quảng Ngãi",
    "Quảng Ninh",
    "Quảng Trị",
    "Sóc Trăng",
    "Sơn La",
    "Tây Ninh",
    "Thái Bình",
    "Thái Nguyên",
    "Thanh Hóa",
    "Thừa Thiên Huế",
    "Tiền Giang",
    "Trà Vinh",
    "Tuyên Quang",
    "Vĩnh Long",
    "Vĩnh Phúc",
    "Yên Bái",
  ];

  useEffect(() => {
    const handleFetch = async () => {
      try {
        setLoading(true);
        const [userResult, classResult] = await Promise.allSettled([
          getUserInfoById(),
          getMyClasses(),
        ]);

        if (userResult.status === "fulfilled" && userResult.value?.data) {
          const user = userResult.value.data;
          setUserInfo(user);
          setFormData({
            fullName: user.fullName || "",
            email: user.email || "",
            phone: user.phone || "",
            school: user.school || "",
            province: user.province || "",
          });
        } else {
          const message = userResult?.reason?.response?.data?.message || "Không thể tải thông tin người dùng";
          toast.error(message);
        }

        if (classResult.status === "fulfilled") {
          setMyClasses(Array.isArray(classResult.value?.data) ? classResult.value.data : []);
        }
      } catch (error) {
        console.log(error);
        const message = error?.response?.data?.message;
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };
    handleFetch();
  }, []);

  const refreshMyClasses = async () => {
    const response = await getMyClasses();
    setMyClasses(Array.isArray(response?.data) ? response.data : []);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const response = await updateInfoUser(formData);
      if (response) {
        toast.success(response?.message);
      }
    } catch (error) {
      console.log(error);
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current) { toast.error("Vui lòng nhập mật khẩu hiện tại"); return; }
    if (!pwForm.newPw) { toast.error("Vui lòng nhập mật khẩu mới"); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error("Mật khẩu xác nhận không khớp"); return; }
    try {
      const response = await updatePassword(pwForm.current, pwForm.newPw);
      if (response) {
        toast.success(response?.message);
        setPwForm({ current: "", newPw: "", confirm: "" });
      }
    } catch (error) {
      console.log(error);
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  };

  const handleJoinInputChange = (e) => {
    const { name, value } = e.target;
    setJoinClassForm((prev) => ({
      ...prev,
      [name]: name === "classCode" ? value.toUpperCase() : value,
    }));
  };

  const handleJoinClass = async (e) => {
    e.preventDefault();

    const classCode = String(joinClassForm.classCode || "").trim().toUpperCase();
    const pin = String(joinClassForm.pin || "").trim();

    if (!classCode) {
      toast.error("Vui lòng nhập mã lớp");
      return;
    }

    if (!pin) {
      toast.error("Vui lòng nhập mã PIN lớp");
      return;
    }

    try {
      setJoiningClass(true);
      const response = await joinPrivateClass(classCode, pin);
      toast.success(response?.message || "Tham gia lớp thành công");
      await refreshMyClasses();
      setJoinClassForm({ classCode: "", pin: "" });
    } catch (error) {
      console.log(error);
      const message = error?.response?.data?.message || "Tham gia lớp thất bại";
      toast.error(message);
    } finally {
      setJoiningClass(false);
    }
  };

  const handleLeaveClass = (classItem) => {
    if (!classItem?._id) {
      toast.error("Không tìm thấy lớp để rời");
      return;
    }

    setLeaveConfirmClass(classItem);
  };

  const handleConfirmLeaveClass = async () => {
    if (!leaveConfirmClass?._id) {
      toast.error("Không tìm thấy lớp để rời");
      return;
    }

    try {
      setLeavingClassId(leaveConfirmClass._id);
      const response = await leaveMyClass(leaveConfirmClass._id);
      toast.success(response?.message || "Rời lớp thành công");
      await refreshMyClasses();
      setLeaveConfirmClass(null);
    } catch (error) {
      console.log(error);
      const message = error?.response?.data?.message || "Rời lớp thất bại";
      toast.error(message);
    } finally {
      setLeavingClassId("");
    }
  };



  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <UserSidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      <main className="lg:ml-72 min-h-screen flex flex-col transition-all duration-300">
        <div className="flex-grow max-w-7xl mx-auto w-full pt-16 pb-8 px-4 sm:px-6 lg:px-8 lg:pt-10">
          <div className="text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:underline transition-colors">
              Trang chủ
            </Link>{" "}
            /{" "}
            <span className="text-gray-800 font-medium">Thông tin cá nhân</span>
          </div>
          
          {/* Mobile Header Toggle */}
          <div className="lg:hidden flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Thông tin cá nhân</h1>
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-600"
            >
              <Menu size={24} />
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            {/* Header */}
            <div className="hidden lg:block px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Thông tin cá nhân
              </h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Change Avatar */}
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Mã số học viên:
                    </p>
                    <p className="text-lg font-semibold text-red-600">
                      {userInfo?._id}
                    </p>
                  </div>

                  {/* Đổi mật khẩu */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700">Đổi mật khẩu</p>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type={showPassword.current ? "text" : "password"}
                        value={pwForm.current}
                        onChange={(e) => setPwForm((p) => ({ ...p, current: e.target.value }))}
                        className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Mật khẩu hiện tại"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword((prev) => ({ ...prev, current: !prev.current }))
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword.current ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                      >
                        {showPassword.current ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type={showPassword.newPw ? "text" : "password"}
                        value={pwForm.newPw}
                        onChange={(e) => setPwForm((p) => ({ ...p, newPw: e.target.value }))}
                        className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Mật khẩu mới"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword((prev) => ({ ...prev, newPw: !prev.newPw }))
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword.newPw ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                      >
                        {showPassword.newPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type={showPassword.confirm ? "text" : "password"}
                        value={pwForm.confirm}
                        onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                        className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Xác nhận mật khẩu mới"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword((prev) => ({ ...prev, confirm: !prev.confirm }))
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword.confirm ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                      >
                        {showPassword.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Column - Form */}
                <div className="space-y-4">
                  {/* Tài khoản */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tài khoản *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Full Name"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        readOnly
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        placeholder="Email"
                      />
                    </div>
                  </div>

                  {/* Số điện thoại */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Số điện thoại *
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Phone"
                      />
                    </div>
                  </div>

                  {/* Tên trường */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tên trường
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      <input
                        type="text"
                        name="school"
                        value={formData.school}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="School"
                      />
                    </div>
                  </div>

                  {/* Tỉnh/Thành phố */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tỉnh / Thành phố
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                      <select
                        name="province"
                        value={formData.province}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent appearance-none bg-white"
                      >
                        <option value="">Chọn tỉnh/thành phố</option>
                        {provinces.map((province, index) => (
                          <option key={index} value={province}>
                            {province}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-5 w-5 text-red-500" />
                  <h3 className="text-lg font-semibold text-gray-900">Thông tin lớp học</h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">Lớp đang tham gia</p>

                    {myClasses.length > 0 ? (
                      <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                        {myClasses.map((classItem) => (
                          <div
                            key={classItem?._id}
                            className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                          >
                            <p className="text-sm font-semibold text-gray-900">
                              {classItem?.name || "Không có tên lớp"}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              Mã lớp: <span className="font-medium text-gray-800">{classItem?.classCode || "-"}</span>
                            </p>
                            {classItem?.description ? (
                              <p className="text-xs text-gray-500 mt-1">{classItem.description}</p>
                            ) : null}
                            <div className="mt-3">
                              <button
                                type="button"
                                onClick={() => handleLeaveClass(classItem)}
                                disabled={leavingClassId === classItem?._id}
                                className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {leavingClassId === classItem?._id ? "Đang rời lớp..." : "Rời lớp"}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm text-gray-600">
                        Bạn chưa tham gia lớp nào.
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">Tham gia lớp mới</p>

                    <form onSubmit={handleJoinClass} className="space-y-3">
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          name="classCode"
                          value={joinClassForm.classCode}
                          onChange={handleJoinInputChange}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Nhập mã lớp (VD: AB12CD34)"
                        />
                      </div>

                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="password"
                          name="pin"
                          value={joinClassForm.pin}
                          onChange={handleJoinInputChange}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Nhập mã PIN lớp"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={joiningClass}
                        className="px-5 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {joiningClass ? "Đang tham gia..." : "Tham gia lớp"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={handleChangePassword}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                >
                  Đổi mật khẩu
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                >
                  Lưu
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>

      {leaveConfirmClass ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border border-gray-200 p-5">
            <h4 className="text-lg font-semibold text-gray-900">Xác nhận rời lớp</h4>
            <p className="text-sm text-gray-600 mt-2 leading-6">
              Bạn chắc chắn muốn rời lớp <span className="font-semibold text-gray-900">"{leaveConfirmClass?.name || "Không có tên lớp"}"</span>?
              Hành động này có thể ảnh hưởng quyền truy cập đề private theo lớp.
            </p>

            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setLeaveConfirmClass(null)}
                disabled={Boolean(leavingClassId)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleConfirmLeaveClass}
                disabled={Boolean(leavingClassId)}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {Boolean(leavingClassId) ? "Đang xử lý..." : "Xác nhận rời lớp"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
